import { useCallback, useEffect, useRef, useState } from "react";
import { formatFileSize } from "../../../../utils/formatFileSize";
import { getUploadSizeError } from "../../../../utils/uploadLimits";
import { convertWordToPdfFile, cleanupStaleWordConvertDom } from "./convertWordToPdf";
import {
  isPdfFile,
  isWordFile,
  loadPdfDocument,
  renderPdfPage,
  PDF_WATERMARK_MAX_PAGES,
} from "./renderPdfFirstPage";
import { runWithMinUploadProgress } from "../../../../utils/runWithMinUploadProgress";

/**
 * Shared document file source for Pdf / Word watermark prep.
 * PDF: keeps a live pdf.js doc + progressive page URL cache for card gallery.
 * Word: converts .docx → PDF client-side, then same PDF preview path.
 */
export function useDocumentFileSource({
  acceptKind = "pdf", // "pdf" | "word"
  busy = false,
  previewScale = 1.35,
} = {}) {
  const inputRef = useRef(null);
  const pdfRef = useRef(null);
  const loadGenRef = useRef(0);
  const pageCacheRef = useRef({});
  const prefetchRef = useRef(0);

  const [file, setFile] = useState(null);
  /** PDF used for preview + bake (original upload or Word conversion). */
  const [pdfFile, setPdfFile] = useState(null);
  const [pageUrls, setPageUrls] = useState({});
  const [pageCount, setPageCount] = useState(0);
  const [currentPage, setCurrentPage] = useState(1);
  const [loadingPreview, setLoadingPreview] = useState(false);
  const [loadingPage, setLoadingPage] = useState(false);
  const [converting, setConverting] = useState(false);
  const [convertProgress, setConvertProgress] = useState(null);
  const [error, setError] = useState(null);
  const [uploadProgress, setUploadProgress] = useState(null);

  const accept =
    acceptKind === "word"
      ? ".docx,application/vnd.openxmlformats-officedocument.wordprocessingml.document,.doc,application/msword"
      : ".pdf,application/pdf";

  /** Only the active page — never fall back to page 1 (that makes clicks look like a no-op). */
  const previewUrl = pageUrls[currentPage] || null;

  const destroyPdf = useCallback(async () => {
    const pdf = pdfRef.current;
    pdfRef.current = null;
    pageCacheRef.current = {};
    prefetchRef.current += 1;
    if (!pdf) return;
    try {
      pdf.cleanup?.();
      await pdf.destroy?.();
    } catch {
      // ignore
    }
  }, []);

  const clear = useCallback(() => {
    if (busy || uploadProgress != null) return;
    loadGenRef.current += 1;
    void destroyPdf();
    cleanupStaleWordConvertDom();
    setFile(null);
    setPdfFile(null);
    setPageUrls({});
    setPageCount(0);
    setCurrentPage(1);
    setError(null);
    setLoadingPreview(false);
    setLoadingPage(false);
    setConverting(false);
    setConvertProgress(null);
    setUploadProgress(null);
    if (inputRef.current) inputRef.current.value = "";
  }, [busy, destroyPdf, uploadProgress]);

  const openPicker = useCallback(() => {
    if (busy || uploadProgress != null) return;
    inputRef.current?.click();
  }, [busy, uploadProgress]);

  const ensurePageUrl = useCallback(
    async (pageNumber) => {
      const n = Math.max(1, Math.floor(Number(pageNumber) || 1));
      const cached = pageCacheRef.current[n];
      if (cached) {
        setPageUrls((prev) =>
          prev[n] === cached ? prev : { ...prev, [n]: cached }
        );
        return cached;
      }
      const pdf = pdfRef.current;
      if (!pdf) return null;
      const gen = loadGenRef.current;
      const rendered = await renderPdfPage(pdf, n, {
        scale: previewScale,
        toDataUrl: true,
      });
      if (gen !== loadGenRef.current) return null;
      pageCacheRef.current[n] = rendered.dataUrl;
      setPageUrls((prev) =>
        prev[n] === rendered.dataUrl ? prev : { ...prev, [n]: rendered.dataUrl }
      );
      return rendered.dataUrl;
    },
    [previewScale]
  );

  const prefetchPages = useCallback(
    async (total, gen) => {
      const token = ++prefetchRef.current;
      const limit = Math.min(total, PDF_WATERMARK_MAX_PAGES);
      for (let n = 2; n <= limit; n += 1) {
        if (token !== prefetchRef.current || gen !== loadGenRef.current) return;
        try {
          await ensurePageUrl(n);
        } catch {
          // keep going; individual cards can retry
        }
        // Yield so UI stays responsive while filling the gallery.
        await new Promise((r) => setTimeout(r, 0));
      }
    },
    [ensurePageUrl]
  );

  const loadPdfPreview = useCallback(
    async (nextPdfFile, gen) => {
      const pdf = await loadPdfDocument(nextPdfFile);
      if (gen !== loadGenRef.current) {
        try {
          pdf.cleanup?.();
          await pdf.destroy?.();
        } catch {
          // ignore
        }
        return;
      }
      pdfRef.current = pdf;
      pageCacheRef.current = {};
      const total = pdf.numPages || 1;
      setPageCount(total);
      setPdfFile(nextPdfFile);
      setPageUrls({});

      const first = await renderPdfPage(pdf, 1, {
        scale: previewScale,
        toDataUrl: true,
      });
      if (gen !== loadGenRef.current) return;
      pageCacheRef.current[1] = first.dataUrl;
      setPageUrls({ 1: first.dataUrl });
      setCurrentPage(1);

      void prefetchPages(total, gen);
    },
    [previewScale, prefetchPages]
  );

  const goToPage = useCallback(
    async (pageNumber) => {
      if (busy) return;
      const total = pageCount || 1;
      const next = Math.max(
        1,
        Math.min(total, Math.floor(Number(pageNumber) || 1))
      );

      setCurrentPage(next);
      setError(null);

      // Prefer cache — still switch even if pdf.js doc was dropped.
      if (pageCacheRef.current[next]) {
        await ensurePageUrl(next);
        return;
      }

      if (!pdfRef.current) {
        setError("PDF preview is not ready. Please re-upload the file.");
        return;
      }

      const gen = loadGenRef.current;
      setLoadingPage(true);
      try {
        const url = await ensurePageUrl(next);
        if (!url && gen === loadGenRef.current) {
          setError("Could not render this page.");
        }
      } catch (err) {
        if (gen !== loadGenRef.current) return;
        setError(err?.message || "Could not render this page.");
      } finally {
        if (gen === loadGenRef.current) setLoadingPage(false);
      }
    },
    [busy, pageCount, ensurePageUrl]
  );

  const applyFile = useCallback(
    async (next) => {
      if (!next || busy || uploadProgress != null) return;

      const sizeError = getUploadSizeError(next);
      if (sizeError) {
        setError(sizeError);
        if (inputRef.current) inputRef.current.value = "";
        return;
      }

      const ok =
        acceptKind === "word" ? isWordFile(next) : isPdfFile(next);
      if (!ok) {
        setError(
          acceptKind === "word"
            ? "Please choose a Word document (.docx)."
            : "Please choose a PDF file."
        );
        if (inputRef.current) inputRef.current.value = "";
        return;
      }

      const gen = ++loadGenRef.current;
      setError(null);
      setPdfFile(null);
      setPageUrls({});
      setPageCount(0);
      setCurrentPage(1);
      setConvertProgress(null);
      await destroyPdf();

      try {
        await runWithMinUploadProgress({
          setProgress: setUploadProgress,
          minMs: 1400,
          work: async () => {
            setFile(next);
            setLoadingPreview(true);
            let nextPdf = next;

            if (acceptKind === "word") {
              setConverting(true);
              nextPdf = await convertWordToPdfFile(next, {
                onProgress: (p) => {
                  if (gen === loadGenRef.current) setConvertProgress(p);
                },
              });
              if (gen !== loadGenRef.current) return;
              setConverting(false);
              setConvertProgress(null);
            }

            await loadPdfPreview(nextPdf, gen);
            if (gen === loadGenRef.current) setLoadingPreview(false);
          },
        });
      } catch (err) {
        if (gen !== loadGenRef.current) return;
        setError(
          err?.message ||
            (acceptKind === "word"
              ? "Could not convert this Word document."
              : "Could not preview this PDF.")
        );
        setPageUrls({});
        setPageCount(0);
        setPdfFile(null);
        setFile(null);
        await destroyPdf();
        setUploadProgress(null);
      } finally {
        if (gen === loadGenRef.current) {
          setLoadingPreview(false);
          setConverting(false);
        }
      }
    },
    [acceptKind, busy, destroyPdf, loadPdfPreview, uploadProgress]
  );

  const onInputChange = useCallback(
    (e) => {
      const next = e.target.files?.[0] || null;
      if (!next) return;
      void applyFile(next);
    },
    [applyFile]
  );

  const onDropFiles = useCallback(
    (files) => {
      const next = files?.[0] || null;
      if (!next) return;
      void applyFile(next);
    },
    [applyFile]
  );

  useEffect(() => {
    return () => {
      loadGenRef.current += 1;
      void destroyPdf();
      cleanupStaleWordConvertDom();
    };
  }, [destroyPdf]);

  return {
    inputRef,
    accept,
    file,
    pdfFile,
    fileName: file?.name || null,
    fileSizeLabel: file ? formatFileSize(file.size) : null,
    previewUrl,
    pageUrls,
    pageCount,
    currentPage,
    loadingPreview,
    loadingPage,
    converting,
    convertProgress,
    uploadProgress,
    error,
    hasFile: Boolean(file),
    clear,
    openPicker,
    onInputChange,
    onDropFiles,
    ensurePageUrl,
    goToPage,
  };
}
